import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputTime } from '@/components/forms/form-input-time';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildNameLikeSelectOptions,
  comboBoxOptionsOf,
  requiredMultiSelectOptions,
  requiredSelectOption,
  SelectOption,
  selectOption,
} from '@/components/ui/multi-select-utils';

import {
  coachesTypeaheadSearch,
  studentsTypeaheadSearch,
  useAllLearningPaths,
  useAllSchools,
} from '@/lib/api-call-hooks';
import {
  createClassTime,
  createManualSession,
  createStudentGroup,
  EmptyArray,
  getAdminSessions,
  getSchedules,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';

import { FrequentTimeZones } from '@airreading/common';
import {
  CoachStatus,
  CreateStudentGroupOptions,
  MAX_PAGE_SIZE,
  MinimalSchoolWithOrg,
  Schedule,
  SortedDaysOfWeek,
  StudentGroupStatus,
} from '@airreading/common/data';

const formSchema = z.object({
  originalTeacher: selectOption,
  school: requiredSelectOption('School is required'),
  students: requiredMultiSelectOptions('Please select at least one student'),
  date: z.object({ year: z.number(), month: z.number(), day: z.number() }),
  startTime: z.instanceof(Temporal.PlainTime),
  duration: z.enum(['30', '40']),
  lesson: requiredSelectOption('Lesson is required'),
  learningPath: requiredSelectOption('Learning path is required'),
});

type FormData = z.infer<typeof formSchema>;

export interface CreateSessionModalProps {
  trigger: ReactNode;
  preSelectedSchool?: SelectOption;
}

const CreateSessionForm = ({
  form,
  schoolSelectionDisabled = false,
  allSchools,
}: {
  form: UseFormReturn<FormData>;
  schoolSelectionDisabled?: boolean;
  allSchools: MinimalSchoolWithOrg[];
}) => {
  const queryClient = useQueryClient();
  const selectedSchoolId = form.watch('school')?.value;
  const selectedLearningPathId = form.watch('learningPath')?.value;
  const studentsTypeahead = studentsTypeaheadSearch(
    queryClient,
    selectedSchoolId ? { schools: [selectedSchoolId] } : undefined,
  );

  const { learningPaths } = useAllLearningPaths();

  const selectedLearningPath = useMemo(
    () => learningPaths.find((lp) => lp.id === selectedLearningPathId),
    [learningPaths, selectedLearningPathId],
  );

  const lessonOptions = useMemo(() => {
    if (!selectedLearningPath) return [];
    return selectedLearningPath.learningPathLessons
      .toSorted((a, b) => a.order - b.order)
      .map(({ lesson }) => ({ label: lesson.name, value: lesson.id }));
  }, [selectedLearningPath]);

  useEffect(() => {
    form.setValue('lesson', {
      label: selectedLearningPathId ? 'Select a lesson' : 'Select a learning path first',
      value: '',
    });
  }, [selectedLearningPathId, form]);

  useEffect(() => {
    if (selectedSchoolId) {
      form.setValue('students', []);
    }
  }, [selectedSchoolId, form]);

  return (
    <div className="mt-4 grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <FormComboBox
          form={form}
          name="originalTeacher"
          label="Original teacher"
          triggerProps={{ className: 'w-full' }}
          {...comboBoxOptionsOf(
            coachesTypeaheadSearch(queryClient, { filters: { statuses: [CoachStatus.ready_to_teach] } }),
          )}
        />
      </div>
      <div className="col-span-2">
        <FormComboBox
          form={form}
          name="school"
          label="School"
          triggerProps={{ className: 'w-full', disabled: schoolSelectionDisabled }}
          placeholder="Select school"
          {...comboBoxOptionsOf(allSchools.map((school) => ({ label: school.name, value: school.id })))}
        />
      </div>
      <div className="col-span-2">
        <FormMultiSelect
          form={form}
          name="students"
          label="Students"
          className="w-full"
          multipleSelectorProps={{
            className: 'w-full',
            disabled: !selectedSchoolId,
            placeholder: selectedSchoolId ? 'Search students by name to add to the session' : 'Select a school first',
            onSearch: studentsTypeahead,
          }}
        />
      </div>

      <div>
        <FormInputDate form={form} name="date" label="Date" triggerButtonProps={{ className: 'w-full' }} />
      </div>
      <div>
        <FormInputTime
          form={form}
          name="startTime"
          label="Start time (CT)"
          triggerButtonProps={{ className: 'w-full' }}
          step={Temporal.Duration.from({ minutes: 1 })}
        />
      </div>

      <div className="col-span-2">
        <FormSingleSelect
          form={form}
          name="duration"
          label="Class duration"
          triggerProps={{ className: 'w-full' }}
          options={['30', '40']}
          renderOption={(value) => `${value} minutes`}
        />
      </div>

      <div>
        <FormComboBox
          form={form}
          name="learningPath"
          label="Learning path"
          triggerProps={{ className: 'w-full' }}
          placeholder="Select learning path"
          {...comboBoxOptionsOf(learningPaths.map(buildNameLikeSelectOptions))}
        />
      </div>
      <div>
        <FormComboBox
          form={form}
          name="lesson"
          label="Lesson"
          triggerProps={{
            className: 'w-full',
            disabled: !selectedLearningPathId,
          }}
          {...comboBoxOptionsOf(lessonOptions)}
        />
      </div>
    </div>
  );
};

export const CreateSessionModal = ({ trigger, preSelectedSchool }: CreateSessionModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: allSchools = EmptyArray } = useAllSchools();
  const { mutateAsync: createManualSessionMutate } = useMutation({ mutationFn: createManualSession });
  const { mutateAsync: createStudentGroupMutate } = useMutation({ mutationFn: createStudentGroup });
  const { mutateAsync: createClassTimeMutate } = useMutation({ mutationFn: createClassTime });

  const findOrCreateClassTime = async (
    startPlainTime: Temporal.PlainTime,
    endPlainTime: Temporal.PlainTime,
    schedules: Schedule[],
    plainDate: Temporal.PlainDate,
  ): Promise<string> => {
    const day = SortedDaysOfWeek[plainDate.dayOfWeek - 1];

    const existingClassTime = schedules
      .flatMap((schedule) => schedule.classTimes)
      .find(
        (ct) =>
          ct.type === 'one_time' &&
          ct.meetingBlocks.length === 1 &&
          ct.meetingBlocks[0].startPlainTime.toString() === startPlainTime.toString() &&
          ct.meetingBlocks[0].endPlainTime.toString() === endPlainTime.toString(),
      );

    if (existingClassTime) {
      return existingClassTime.id;
    }

    const schedule = schedules[0];
    const newClassTime = await createClassTimeMutate({
      scheduleId: schedule.id,
      meetingBlocks: [
        {
          day: day,
          startPlainTime: startPlainTime,
          endPlainTime: endPlainTime,
        },
      ],
      type: 'one_time',
    });
    return newClassTime.id;
  };

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<FormData>) => {
    try {
      const selectedSchoolId = formData.school.value;
      const schoolTimeZone = allSchools.find((s) => s.id === selectedSchoolId)?.org.timeZone;

      if (!schoolTimeZone) {
        toast({
          title: 'Missing school time zone',
          description:
            'Selected school does not have a time zone configured. Please set the time zone for the school in district settings first.',
          variant: 'destructive',
        });
        return;
      }

      // Fetch schedules for the selected school using the queryFn pattern
      const schedulesQuery = getSchedules({ pageIndex: 0, pageSize: MAX_PAGE_SIZE, schoolId: selectedSchoolId });
      const schedulesResponse = await schedulesQuery.queryFn();
      const schedules: Schedule[] = schedulesResponse.schedules || EmptyArray;

      if (!schedules.length) {
        toast({
          title: 'No schedule configured',
          description: 'Please ensure the school has a schedule configured before creating sessions.',
          variant: 'destructive',
        });
        return;
      }

      // Calculate session times from input time and date
      const plainDate = Temporal.PlainDate.from(formData.date);
      const startTime = Temporal.PlainTime.from(formData.startTime);
      const durationMinutes = parseInt(formData.duration);
      const start = plainDate.toZonedDateTime({ plainTime: startTime, timeZone: FrequentTimeZones.UsCentral });
      const end = start.add({ minutes: durationMinutes });
      const startPlainTimeInSchoolTimeZone = start.withTimeZone(schoolTimeZone).toPlainTime();
      const endPlainTimeInSchoolTimeZone = end.withTimeZone(schoolTimeZone).toPlainTime();

      // Find or create the appropriate class time. For class time, we store plain time in school time zone
      const classTimeId = await findOrCreateClassTime(
        startPlainTimeInSchoolTimeZone,
        endPlainTimeInSchoolTimeZone,
        schedules,
        plainDate,
      );

      // Create student group
      const studentGroupPayload = {
        studentIds: formData.students.map((s) => s.value),
        learningPathId: formData.learningPath.value,
        startingLessonId: formData.lesson.value,
        status: StudentGroupStatus.active,
        classTimeId: classTimeId,
        ...(formData.originalTeacher.value && {
          coachId: formData.originalTeacher.value,
        }),
        overrideStartPlainDate: plainDate,
        overrideEndPlainDate: plainDate,
      } satisfies CreateStudentGroupOptions;

      const studentGroup = await createStudentGroupMutate(studentGroupPayload);

      // Create session
      const sessionPayload = {
        studentGroupId: studentGroup.id,
        data: {
          coachId: formData.originalTeacher.value,
          studentIds: formData.students.map((s) => s.value),
          lessonId: formData.lesson.value,
          start: start.toInstant().toString(),
          end: end.toInstant().toString(),
        },
      };

      await createManualSessionMutate(sessionPayload);

      toast({
        title: 'Session created successfully',
        description: 'The new session has been created.',
      });

      closeModal();
      reset();
      queryClient.invalidateQueries(getAdminSessions());
    } catch (error) {
      const errorMessage =
        airServerErrorFrom(error)?.userFacingMessage ??
        (error instanceof Error ? error.message : 'There was an error creating the session. Please try again.');

      toast({
        title: 'Failed to create session',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Add session"
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      defaultValues={{
        students: [],
        startTime: Temporal.PlainTime.from('09:00:00'),
        duration: '30',
        school: preSelectedSchool ?? undefined,
      }}
      submitButtonProps={{
        className: 'bg-black text-white hover:bg-gray-900',
      }}
      confirmBtnLabel="Add"
      cancelBtnLabel="Cancel"
      dialogContentProps={{ className: 'max-w-2xl' }}
    >
      {({ form }) => (
        <CreateSessionForm form={form} schoolSelectionDisabled={!!preSelectedSchool} allSchools={allSchools} />
      )}
    </FormModal>
  );
};
